import React, { useCallback, useEffect, useState } from "react";
import Modal from "../../components/Atomos/Modals/Modal";
import LegacyButton from "../../components/Atomos/Buttons/LegacyButton";
import type { AccountingFirmOfficeGroup } from "../../utils/accountingOfficeTableGrouping";
import {
  countNonAttorneyByOffices,
  countPersonsByOffices,
} from "../../Services/Person";
import { countFirmPublishedContents } from "../../Services/Content";
import { getFirmGroupStatistics } from "../../Services/Group";
import {
  buildOfficeStatisticsRows,
  type OfficeStatisticsRow,
} from "./officeStatisticsRows";

type OfficeStatisticsModalProps = {
  isOpen: boolean;
  officeId: string;
  firmId: string;
  officeLabel: string;
  firmOfficeIndex: Map<string, AccountingFirmOfficeGroup>;
  onClose: () => void;
};

function parseCountResult(data: unknown): number {
  if (Array.isArray(data) && data.length > 0) {
    const first = Number(data[0]);
    return Number.isFinite(first) ? first : 0;
  }
  const single = Number(data);
  return Number.isFinite(single) ? single : 0;
}

const OfficeStatisticsModal: React.FC<OfficeStatisticsModalProps> = ({
  isOpen,
  officeId,
  firmId,
  officeLabel,
  firmOfficeIndex,
  onClose,
}) => {
  const [rows, setRows] = useState<OfficeStatisticsRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetState = useCallback(() => {
    setRows([]);
    setLoading(false);
    setError(null);
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  useEffect(() => {
    const normalizedOfficeId = officeId.trim();
    const normalizedFirmId = firmId.trim();
    if (!isOpen || !normalizedOfficeId || !normalizedFirmId) {
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setRows([]);

    const officeCount =
      firmOfficeIndex.get(normalizedFirmId)?.offices.length ?? 0;

    void Promise.all([
      countPersonsByOffices(normalizedOfficeId),
      countNonAttorneyByOffices(normalizedOfficeId, normalizedFirmId),
      countFirmPublishedContents(normalizedFirmId, "press"),
      countFirmPublishedContents(normalizedFirmId, "article"),
      getFirmGroupStatistics(normalizedFirmId),
    ])
      .then(
        ([
          professionalsResult,
          staffResult,
          pressCount,
          articleCount,
          groupStats,
        ]) => {
        if (cancelled) {
          return;
        }
        setRows(
          buildOfficeStatisticsRows({
            officeCount,
            professionals: parseCountResult(professionalsResult),
            staff: parseCountResult(staffResult),
            pressReleases: pressCount,
            publishedArticles: articleCount,
            distinctOpenGroups: groupStats.distinctOpenGroups,
            distinctPrivateGroups: groupStats.distinctPrivateGroups,
            distinctOpenGroupsParticipants:
              groupStats.distinctOpenGroupsParticipants,
            distinctPrivateGroupsParticipants:
              groupStats.distinctPrivateGroupsParticipants,
          }),
        );
      },
      )
      .catch(() => {
        if (!cancelled) {
          setError("Could not load office statistics.");
          setRows(
            buildOfficeStatisticsRows({
              officeCount,
              professionals: 0,
              staff: 0,
              pressReleases: 0,
              publishedArticles: 0,
              distinctOpenGroups: 0,
              distinctPrivateGroups: 0,
              distinctOpenGroupsParticipants: 0,
              distinctPrivateGroupsParticipants: 0,
            }),
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, officeId, firmId, firmOfficeIndex]);

  const title = officeLabel.trim()
    ? `Office Statistics — ${officeLabel.trim()}`
    : "Office Statistics";

  return (
    <Modal
      isModalOpen={isOpen}
      toggleModal={handleClose}
      title={title}
      compact
      className="max-w-md w-full"
    >
      <div className="flex flex-col gap-4">
        {error && !loading ? (
          <p className="text-sm text-red-600">{error}</p>
        ) : null}

        {rows.length > 0 ? (
          <table className="w-full table-fixed border border-neutral-200 text-base text-gray-font">
            <tbody>
              {rows.map((row) => (
                <tr key={row.label} className="border-b border-neutral-200 last:border-b-0">
                  <td className="px-3 py-2">{row.label}</td>
                  <td className="w-20 px-3 py-2 text-right font-semibold tabular-nums">
                    {row.value}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}

        <div className="mt-2 flex justify-end border-t border-neutral-200 pt-4">
          <LegacyButton
            text="Close"
            style="outlined"
            size="medium"
            content="text"
            onClick={handleClose}
            color="neutral"
            disabled={loading}
            className="!w-auto shrink-0"
          />
        </div>
      </div>
    </Modal>
  );
};

export default OfficeStatisticsModal;
